/**
 * Permission Cache Utility
 *
 * Caches user permissions in sessionStorage to avoid repeated API calls
 * Automatically refreshes when stale or missing
 */

import DataApi from '../api/DataApi';
import jwt_decode from 'jwt-decode';

const CACHE_KEY = 'user_permissions';
const CACHE_TIMESTAMP_KEY = 'user_permissions_timestamp';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes (match backend cache)

/**
 * Check if user is admin
 */
export const isAdmin = (userInfo) => {
  if (!userInfo) {
    console.log('🔍 [isAdmin] No userInfo provided');
    return false;
  }

  const roleName = userInfo.role_name?.toUpperCase();
  const userRole = userInfo.userrole?.toUpperCase();

  console.log('🔍 [isAdmin] Checking admin status:', {
    role_name: userInfo.role_name,
    roleName_upper: roleName,
    userrole: userInfo.userrole,
    userRole_upper: userRole
  });

  const isAdminUser = roleName === 'ADMIN' ||
    roleName === 'SUPER_ADMIN' ||
    userRole === 'ADMIN' ||
    userRole === 'SUPER_ADMIN';

  console.log(`🔍 [isAdmin] Result: ${isAdminUser}`);
  return isAdminUser;
};

/**
 * Get cached permissions or fetch from API
 */
export const getUserPermissions = async (forceRefresh = false) => {
  try {
    console.log('🔍 [Permission Cache] === GET PERMISSIONS STARTED ===');
    console.log(`🔍 [Permission Cache] Force refresh: ${forceRefresh}`);

    // Get user info from session or decode from token
    let userInfoStr = sessionStorage.getItem('userinfo');
    console.log('🔍 [Permission Cache] Raw userinfo from sessionStorage:', userInfoStr);

    // If userinfo not in sessionStorage, decode from token in localStorage
    if (!userInfoStr) {
      console.log('🔍 [Permission Cache] No userinfo in sessionStorage, checking token...');
      const token = localStorage.getItem('token');

      if (!token) {
        console.error('❌ [Permission Cache] No token in localStorage');
        return [];
      }

      try {
        const decodedToken = jwt_decode(token);
        console.log('🔍 [Permission Cache] Decoded token:', decodedToken);

        // Store in sessionStorage for future use
        sessionStorage.setItem('userinfo', JSON.stringify(decodedToken));
        userInfoStr = JSON.stringify(decodedToken);
        console.log('✅ [Permission Cache] Reconstructed userinfo from token');
      } catch (error) {
        console.error('❌ [Permission Cache] Error decoding token:', error);
        return [];
      }
    }

    const userInfo = JSON.parse(userInfoStr);
    console.log('🔍 [Permission Cache] Parsed userInfo:', userInfo);
    console.log('🔍 [Permission Cache] Role info from userInfo:', {
      role_id: userInfo.role_id,
      role_name: userInfo.role_name,
      userrole: userInfo.userrole
    });

    // Admin bypass - no need to fetch permissions
    if (isAdmin(userInfo)) {
      console.log('🔓 [Permission Cache] ADMIN user - bypassing permission fetch');
      return []; // Admin doesn't need permissions checked
    }

    const roleId = userInfo.role_id;
    console.log(`🔍 [Permission Cache] Extracted role_id: ${roleId}`);

    if (!roleId) {
      console.error('❌ [Permission Cache] User has no role_id in userinfo');
      console.error('❌ [Permission Cache] Full userInfo object:', JSON.stringify(userInfo, null, 2));
      return [];
    }

    // Check cache validity
    const cachedPermissions = sessionStorage.getItem(CACHE_KEY);
    const cachedTimestamp = sessionStorage.getItem(CACHE_TIMESTAMP_KEY);
    const cacheAge = cachedTimestamp ? Date.now() - parseInt(cachedTimestamp) : Infinity;

    console.log('🔍 [Permission Cache] Cache status:', {
      hasCached: !!cachedPermissions,
      cacheAge: Math.floor(cacheAge / 1000) + 's',
      isStale: cacheAge >= CACHE_TTL
    });

    if (!forceRefresh && cachedPermissions && cacheAge < CACHE_TTL) {
      console.log('✅ [Permission Cache] Using cached permissions');
      const parsed = JSON.parse(cachedPermissions);
      console.log(`✅ [Permission Cache] Cached permissions count: ${parsed.length}`);
      return parsed;
    }

    // Fetch fresh permissions
    console.log(`🔄 [Permission Cache] Fetching fresh permissions from API for role ${roleId}`);
    const permissionApi = new DataApi('permissions');
    const response = await permissionApi.get(`/role/${roleId}`);
    console.log('🔍 [Permission Cache] API response:', response.data);

    if (response.data.success) {
      const permissions = response.data.data || [];

      // Cache the permissions
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(permissions));
      sessionStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());

      console.log(`✅ [Permission Cache] Cached ${permissions.length} permissions`);
      console.log('✅ [Permission Cache] Permissions:', permissions);
      console.log('🔍 [Permission Cache] === GET PERMISSIONS COMPLETE ===');
      return permissions;
    }

    console.error('❌ [Permission Cache] API returned unsuccessful response');
    return [];
  } catch (error) {
    console.error('❌ [Permission Cache] Error fetching permissions:', error);
    console.error('❌ [Permission Cache] Error details:', error.message, error.stack);
    return [];
  }
};

/**
 * Check if user has permission for an object and action
 */
export const hasPermission = async (objectName, action = 'read') => {
  try {
    // Get user info from session or decode from token
    let userInfoStr = sessionStorage.getItem('userinfo');

    // If userinfo not in sessionStorage, decode from token in localStorage
    if (!userInfoStr) {
      const token = localStorage.getItem('token');
      if (!token) return false;

      try {
        const decodedToken = jwt_decode(token);
        sessionStorage.setItem('userinfo', JSON.stringify(decodedToken));
        userInfoStr = JSON.stringify(decodedToken);
      } catch (error) {
        console.error('Error decoding token:', error);
        return false;
      }
    }

    const userInfo = JSON.parse(userInfoStr);

    // Admin bypass
    if (isAdmin(userInfo)) {
      return true;
    }

    // Get permissions (match object_name case-insensitively: URL may be "lead", DB "Lead")
    const permissions = await getUserPermissions();
    const objectNameLower = (objectName || '').toLowerCase();
    const permission = permissions.find(
      p => (p.object_name || '').toLowerCase() === objectNameLower
    );

    if (!permission || !permission.is_active) {
      return false;
    }

    const { can_read, can_create, can_update, can_delete, view_all, modify_all } = permission;

    switch (action) {
      case 'read':
      case 'view':
        return can_read || view_all || modify_all;
      case 'create':
        return can_create || modify_all;
      case 'update':
      case 'edit':
        return can_update || modify_all;
      case 'delete':
        return can_delete || modify_all;
      default:
        return false;
    }
  } catch (error) {
    console.error('Error checking permission:', error);
    return false;
  }
};

/**
 * Clear permission cache (call after logout or permission changes)
 */
export const clearPermissionCache = () => {
  sessionStorage.removeItem(CACHE_KEY);
  sessionStorage.removeItem(CACHE_TIMESTAMP_KEY);
  console.log('🗑️  [Permission Cache] Cleared');
};

/**
 * Preload permissions on login (call after successful login)
 */
export const preloadPermissions = async () => {
  try {
    await getUserPermissions(true); // Force refresh
    console.log('✅ [Permission Cache] Permissions preloaded');
  } catch (error) {
    console.error('❌ [Permission Cache] Error preloading permissions:', error);
  }
};
